import {
  BooleanValueSchema,
  ComparisonOperators,
  DateTimeValueSchemas,
  IntegerValueSchema,
  LogicalOperators,
  NumberValueSchema,
  StringValueSchema,
  type LogicalOperatorsType,
} from "@validereinc/utilities";
import { validate as uuidValidate } from "uuid";
import { z, type UnknownKeysParam } from "zod";

/**
 * Formats a value to support the mongo-like syntax the backend expects.
 *
 * @param value The atomic value we want to send to the backend
 * @returns A filter value the backend can consume for filter endpoints
 */
export const getFilterValue = <T>(value: T | T[] | { $exact: T }) => {
  const isSingleStringFilterValue = typeof value === "string" && value !== "";

  const isBooleanFilterValue = typeof value === "boolean";

  const isNumberFilterValue = typeof value === "number";

  const isMultiStringFilterValue =
    Array.isArray(value) &&
    value?.length &&
    value.every((v) => typeof v === "string" && v !== "");

  const isExactFilterValue =
    value &&
    typeof value === "object" &&
    "$exact" in value &&
    value.$exact !== "";

  const isEmptyArrayFilterValue = Array.isArray(value) && !value?.length;

  if (
    !isSingleStringFilterValue &&
    !isBooleanFilterValue &&
    !isMultiStringFilterValue &&
    !isExactFilterValue &&
    !isEmptyArrayFilterValue &&
    !isNumberFilterValue
  ) {
    return;
  }

  if (isMultiStringFilterValue) {
    return { $in: value };
  }

  if (isEmptyArrayFilterValue) {
    return;
  }

  if (isSingleStringFilterValue) {
    // An exception is made for a string that is a valid uuid since the $like syntax won't work
    if (uuidValidate(value)) {
      return value;
    }

    // REVIEW: this is problematic as we don't want to force fuzzy searching
    // always for string values
    return { $like: value };
  }

  if (isExactFilterValue) {
    return value.$exact;
  }

  return value;
};

/**
 * Formats an object's values to support the mongo-like syntax the backend expects.
 *
 * @param filters An object containing key/value pairs we want to filter on
 * @returns A filter object the backend can consume for filter endpoints
 */
export const getFilterObject = <T>(
  filters?: FilterObjectType<T> & { isAlreadyFormatted?: boolean }
) => {
  if (!filters) {
    return {};
  }
  const { isAlreadyFormatted, ...filterValues } = filters;
  if (isAlreadyFormatted) {
    return filterValues;
  }

  return Object.entries(filterValues).reduce(
    (total, [key, value]) => {
      if (key === LogicalOperators.and) {
        if (Object.hasOwn(total, LogicalOperators.and)) {
          return { ...total, $and: [...total.$and, ...value] };
        }

        return { ...total, [key]: value };
      }

      if (key === LogicalOperators.or) {
        return { ...total, [key]: value };
      }

      const dateTimeRangeResult =
        DateTimeValueSchemas.dateTimeRange.safeParse(value);

      if (dateTimeRangeResult.success) {
        const { from, to } = dateTimeRangeResult.data;

        if (Object.hasOwn(total, LogicalOperators.and)) {
          return {
            ...total,
            [LogicalOperators.and]: [
              ...total.$and,
              { [key]: { [ComparisonOperators.gte]: from } },
              { [key]: { [ComparisonOperators.lte]: to } },
            ],
          };
        }

        return {
          ...total,
          [LogicalOperators.and]: [
            { [key]: { [ComparisonOperators.gte]: from } },
            { [key]: { [ComparisonOperators.lte]: to } },
          ],
        };
      }

      return { ...total, [key]: getFilterValue(value) };
    },
    {} as Record<string, any>
  );
};

// --- Filtering utility schemas and types. Note, not all the types infer cleanly from the Zod schemas, so some have to be constructed manually. ---
export const FilterLogicalOperators = { ...LogicalOperators } as const;
export const FilterLogicalOperatorsSchema = z.nativeEnum(LogicalOperators);
export type FilterLogicalOperatorsType = z.infer<
  typeof FilterLogicalOperatorsSchema
>;

export const FilterComparisonOperators = { ...ComparisonOperators } as const;
export const FilterComparisonOperatorsSchema =
  z.nativeEnum(ComparisonOperators);
export type FilterComparisonOperatorsType = z.infer<
  typeof FilterComparisonOperatorsSchema
>;

export const FilterValuePrimitiveSchema = z.union([
  StringValueSchema,
  IntegerValueSchema,
  NumberValueSchema,
  BooleanValueSchema,
]);
export type FilterValuePrimitiveType = z.infer<
  typeof FilterValuePrimitiveSchema
>;

export const FilterValuePlusSchema = z.union([
  z.array(StringValueSchema),
  z.array(IntegerValueSchema),
  z.array(NumberValueSchema),
  z.array(BooleanValueSchema),
]);

export const FilterValuePrimitivePlusSchema = z.union([
  FilterValuePrimitiveSchema,
  FilterValuePlusSchema,
]);
export type FilterValuePrimitivePlusType = z.infer<
  typeof FilterValuePrimitivePlusSchema
>;

const FilterValueStringComparisonSchema = z
  .object({
    [ComparisonOperators.exact]: z.string(),
  })
  .or(
    z.object({
      [ComparisonOperators.like]: z.string(),
    })
  );

const FilterValueArrayComparisonSchema = z
  .object({
    [ComparisonOperators.in]: FilterValuePlusSchema,
  })
  .or(
    z.object({
      [ComparisonOperators.all]: FilterValuePlusSchema,
    })
  );

const FilterValueNumberComparisonSchema = z
  .object({
    [ComparisonOperators.gt]: z.union([
      z.number(),
      DateTimeValueSchemas.dateTime,
    ]),
  })
  .or(
    z.object({
      [ComparisonOperators.gte]: z.union([
        z.number(),
        DateTimeValueSchemas.dateTime,
      ]),
    })
  )
  .or(
    z.object({
      [ComparisonOperators.lt]: z.union([
        z.number(),
        DateTimeValueSchemas.dateTime,
      ]),
    })
  )
  .or(
    z.object({
      [ComparisonOperators.lte]: z.union([
        z.number(),
        DateTimeValueSchemas.dateTime,
      ]),
    })
  );

const FilterValueBooleanComparisonSchema = z.object({
  [ComparisonOperators.exists]: z.boolean(),
});

const FilterValueEqualityComparisonSchema = z
  .object({
    [ComparisonOperators.eq]: FilterValuePrimitivePlusSchema,
  })
  .or(
    z.object({
      [ComparisonOperators.ne]: FilterValuePrimitivePlusSchema,
    })
  );

type FilterValueWithComparisonOperatorsType =
  | { $exact: string }
  | { $like: string }
  | { $in: FilterValuePrimitiveType[] }
  | { $all: FilterValuePrimitiveType[] }
  | { $gt: number | string }
  | { $gte: number | string }
  | { $lt: number | string }
  | { $lte: number | string }
  | { $exists: boolean }
  | { $eq: FilterValuePrimitiveType }
  | { $ne: FilterValuePrimitiveType };

/**
 * Represents a filter value that is an explicit comparison operator (i.e. excludes implicit $eq with primitive values {@link FilterValuePrimitivePlusSchema})
 *
 * The value is a subject with an explicit comparison to apply on a
 * property of a domain model, which is defined by the filter key.
 */
export const FilterValueWithComparisonOperatorSchema = z
  .union([
    FilterValueStringComparisonSchema,
    FilterValueNumberComparisonSchema,
    FilterValueBooleanComparisonSchema,
    FilterValueArrayComparisonSchema,
    FilterValueEqualityComparisonSchema,
  ])
  .describe(
    "Represents a filter value with an explicit comparison operator. The value is a subject with an explicit comparison to apply on a property of a domain model, which is defined by the filter key"
  );

type FilterValueWithComparisonOperatorType<T> = T extends string
  ? {
      $exact?: string;
      $like?: string;
      $gt?: string;
      $gte?: string;
      $lt?: string;
      $lte?: string;
      $eq?: string;
      $ne?: string;
      $in?: FilterValuePrimitiveType[];
      $all?: FilterValuePrimitiveType[];
    }
  : T extends number
    ? {
        $gt?: number;
        $gte?: number;
        $lt?: number;
        $lte?: number;
        $eq?: number;
        $ne?: number;
        $in?: FilterValuePrimitiveType[];
        $all?: FilterValuePrimitiveType[];
      }
    : T extends boolean
      ? {
          $exists?: boolean;
          $eq?: boolean;
          $ne?: boolean;
          $in?: FilterValuePrimitiveType[];
          $all?: FilterValuePrimitiveType[];
        }
      : T extends string[] | number[] | boolean[]
        ? {
            $in?: FilterValuePrimitiveType[];
            $all?: FilterValuePrimitiveType[];
          }
        : never;

// filter keys cannot start with $ (only logical operators can show up as a key in the filter object)
type FilterValueWithComparisonOperatorKeyType<T> = {
  [K in keyof T]: K extends `$${string}` ? never : K;
}[keyof T];

/**
 * Represents a filter value.
 *
 * The value is a subject with an implicit or explicit comparison to apply on a
 * property of a domain model, which is defined by the filter key.
 */
export const FilterValueSchema = z
  .union([
    FilterValuePrimitivePlusSchema,
    FilterValueStringComparisonSchema,
    FilterValueNumberComparisonSchema,
    FilterValueBooleanComparisonSchema,
    FilterValueArrayComparisonSchema,
    FilterValueEqualityComparisonSchema,
  ])
  .describe(
    "Represents a filter value. The value is a subject with an implicit or explicit comparison to apply on a property of a domain model, which is defined by the filter key"
  );
/**
 * Represents a filter value.
 *
 * The value is a subject with an implicit or explicit comparison to apply on
 * a property of a domain model, which is defined by the filter key.
 */
export type FilterValueType =
  // there is an implicit $eq comparison operator applied for this
  | FilterValuePrimitivePlusType
  // or it can be an object with any of the possible comparison operators used
  | FilterValueWithComparisonOperatorsType;

type FilterObjectWithComparisonOperatorsType<T> = {
  [K in FilterValueWithComparisonOperatorKeyType<T>]?: FilterValueType;
};

export const FilterObjectWithLogicalOperatorsSchema = z
  .object({
    $and: z.array(z.lazy(() => FilterObjectSchema)).min(1),
  })
  .passthrough()
  .or(
    z
      .object({
        $or: z.array(z.lazy(() => FilterObjectSchema)).min(1),
      })
      .passthrough()
  )
  .or(
    z
      .object({
        $not: z.array(z.lazy(() => FilterObjectSchema)).min(1),
      })
      .passthrough()
  );

type FilterObjectWithLogicalOperatorsType<T> = {
  [K in FilterLogicalOperatorsType]?: Array<FilterObjectType<T>>;
};

/**
 * Represents a filter object, a series of key value pairs that can be grouped
 * together using logical operators, where the key is a property on some domain
 * model or reference to one, and where the value is a subject with an implicit
 * or explicit comparison to apply on the property.
 *
 * This can be used in any of the domain adapters.
 */
export const FilterObjectSchema: z.ZodType = z.lazy(() =>
  z
    .object({
      $and: z
        .array(z.lazy(() => FilterObjectSchema))
        .min(1)
        .optional(),
      $or: z
        .array(z.lazy(() => FilterObjectSchema))
        .min(1)
        .optional(),
      $not: z
        .array(z.lazy(() => FilterObjectSchema))
        .min(1)
        .optional(),
    })
    .catchall(FilterValueSchema)
    .refine(
      (obj) => {
        const operatorKeys = Object.keys(obj).filter((key) =>
          key.startsWith("$")
        );

        // every key that starts with $ must be a logical or comparison operator
        return operatorKeys.length
          ? operatorKeys.every((key) =>
              [
                ...Object.values(LogicalOperators),
                ...Object.values(ComparisonOperators),
              ].includes(key as LogicalOperatorsType)
            )
          : true;
      },
      {
        message: "Unknown filter object key",
      }
    )
    .describe(
      "Represents a filter object, a series of key value pairs that can be grouped together using logical operators, where the key is a property on some domain model or reference to one, and where the value is a subject with an implicit or explicit comparison to apply on the property."
    )
);
/**
 * Represents a filter object, a series of key value pairs that can be grouped
 * together using logical operators, where the key is a property on some domain
 * model or reference to one, and where the value is a subject with an implicit
 * or explicit comparison to apply on the property.
 *
 * This can be used in any of the domain adapters.
 */
export type FilterObjectType<T> = FilterObjectWithComparisonOperatorsType<T> &
  FilterObjectWithLogicalOperatorsType<T>;

export const createFilterObjectSchema = <
  T extends z.ZodObject<any, UnknownKeysParam, z.ZodTypeAny>,
>(
  filterSchema: T
) => {
  const filterObjectSchemaWithKeyRefinementAgainstProvidedFilterSchema: z.ZodType =
    z.lazy(() =>
      FilterObjectSchema.refine(
        (obj) => {
          const nonOperatorKeys = Object.keys(obj).filter(
            (key) => !key.startsWith("$")
          );

          // ensure that keys that don't start with $ exist in the provided filter schema
          return nonOperatorKeys.length
            ? nonOperatorKeys.every((key) => key in filterSchema.shape)
            : true;
        },
        {
          message:
            "Unknown filter object key not specified in reference schema",
        }
      )
    );

  // assertion for conformance with the adapter request/response shape factory schema functions
  return filterObjectSchemaWithKeyRefinementAgainstProvidedFilterSchema as z.ZodObject<
    z.infer<T>
  >;
};

/**
 * Type predicate to check if a logical operator exists in a filter object
 */
export const hasLogicalOperatorInFilterObject = <T>(
  value: unknown
): value is FilterObjectWithLogicalOperatorsType<T> => {
  return FilterObjectWithLogicalOperatorsSchema.safeParse(value).success;
};

/**
 * Type predicate to check if a filter value has a comparison operator being used
 */
export const isComparisonOperatorFilterValue = <T>(
  value: unknown
): value is FilterValueWithComparisonOperatorType<T> => {
  return FilterValueWithComparisonOperatorSchema.safeParse(value).success;
};
